import React, { useState, useMemo, useCallback, useEffect } from 'react';

import {
  CourseEnrolment,
  PendingInvitation,
} from 'src/ReduxStorage/InstitutionAdminSettings/types';
import { PortalResourceGetPaginatedPendingInvitations } from 'src/resource/PortalResource/types';
import moment from 'moment';
import { debounce } from 'lodash';
import { Spinner } from 'src/web/components/Spinner/Spinner';
import { CursorPaginate } from 'src/web/components/Pagination/CursorPaginate';
import { Popover } from 'src/web/components/Popover/component';
import { faEnvelope, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { getPaginatedPendingInvitationsApi } from 'src/ReduxStorage/InstitutionAdminSettings/asyncActions';
import { faPlus, faSortDown } from '@fortawesome/pro-solid-svg-icons';
import { pNotifyAlert } from 'src/web/components/Pnotify/component';
import { ActionTable } from '../../../../../components/ActionTable/component';
import { Key, Row, SortBy } from '../../../../../components/ActionTable/types';
import {
  PaginationContainer,
  FilterWrapper,
  StyledTextField,
  ActionTableWrapper,
  SpinnerWrapper,
  BulkActionButton,
  BulkActionSortDownIcon,
  BulkActionContentWrapper,
  BulkActionContentButton,
  NameEmailInfo,
  UserEmail,
  BulkActionIcon,
} from '../../../styles';
import profileImage from '../../../images/profileImage.svg';
import { RemoveFromInstitutionModal } from '../../../components/RemoveFromInstitutionModal/component';
import {
  useUninvitePendingTeamMembersApi,
  useResendInvitationEmailsApi,
  useScheduleAssignInvitedUsersToCourse,
} from '../../../hooks';

import { AssignToCoursesModal } from '../AssignToCoursesModal/component';
import { AssignedCourseRolesType } from '../../../types';
import { ConnectedCoursesAssignedPopover } from '../../../components/CourseAssignedPopover/connector';
import { parseKeyDataPendingMember } from '../../../helpers';

enum ShowedModalType {
  REMOVE_FROM_INSTITUTION = 'REMOVE_FROM_INSTITUTION',
  ASSIGN_TO_COURSE = 'ASSIGN_TO_COURSE',
}
export interface PendingTeamMemberTableProps {
  paginatedPendingInvitations: {
    hasNextPage: boolean;
    invitedUsers: readonly PendingInvitation[];
    total: number;
  };
  isLoading: boolean;
  currentInstitutionPath: string;
  allCourses: CourseEnrolment[];
  filterByUserEmails?: string[];
  getPaginatedPendingInvitations: (
    params: PortalResourceGetPaginatedPendingInvitations['args']
  ) => Promise<unknown>;
}

export const PendingTeamMemberTable: React.FC<PendingTeamMemberTableProps> = ({
  paginatedPendingInvitations,
  currentInstitutionPath,
  allCourses,
  filterByUserEmails,
  isLoading,
  getPaginatedPendingInvitations,
}) => {
  // selectedRows format `${id}-${email}`
  const [selectedRows, setSelectedRows] = useState<Key[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>();
  const [searchUserQuery, setSearchUserQuery] = useState('');
  const [
    showedModalType,
    setShowedModalType,
  ] = useState<ShowedModalType | null>(null);
  // Pagination states
  const [pageSize, setPageSize] = useState(10);
  const [nextItemStack, setNextItemStack] = useState<PendingInvitation[]>([]);

  const [
    pendingInvitationsQueryParams,
    setPendingInvitationsQueryParams,
  ] = useState<PortalResourceGetPaginatedPendingInvitations['args']>({
    staff: true,
    limit: pageSize,
    filterByUserEmails,
  });

  const { uninvitePendingTeamMembers } = useUninvitePendingTeamMembersApi(
    currentInstitutionPath
  );
  const { resendInvitationEmails } = useResendInvitationEmailsApi(
    currentInstitutionPath
  );
  const {
    scheduleAssignInvitedUsersToCourse,
  } = useScheduleAssignInvitedUsersToCourse(currentInstitutionPath, true);

  const debouncedGetPaginatedPendingInvitations = useCallback(
    debounce((args: PortalResourceGetPaginatedPendingInvitations['args']) => {
      getPaginatedPendingInvitations(args);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedGetPaginatedPendingInvitations(pendingInvitationsQueryParams);
  }, [pendingInvitationsQueryParams]);

  const { total, invitedUsers, hasNextPage } = paginatedPendingInvitations;
  const totalSelectedMembers = selectAll ? total : selectedRows.length;

  const refreshCurrentPage = (): void =>
    debouncedGetPaginatedPendingInvitations(pendingInvitationsQueryParams);

  const resetPagination = useCallback((): void => setNextItemStack([]), [
    setNextItemStack,
  ]);

  useEffect(() => {
    let sortingParams: { sortBy?: string; sortDirection?: -1 | 1 } = {};
    if (!sortBy) {
      sortingParams = {
        sortBy: undefined,
        sortDirection: undefined,
      };
    } else {
      sortingParams = {
        sortBy: sortBy.sortKey,
        sortDirection: sortBy.direction === 'ascending' ? 1 : -1,
      };
    }
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      ...sortingParams,
      afterItem: undefined,
    }));
    resetPagination();
  }, [sortBy]);

  const onSearchByUserValueChange = (value: string): void => {
    setSearchUserQuery(value);
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      filterByUser: value,
      afterItem: undefined,
    }));
    resetPagination();
  };

  const onNextPageCursorPaginate = (): void => {
    const lastItem = invitedUsers[invitedUsers.length - 1];
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      afterItem: lastItem,
    }));
    // Push latest nextItem to the stack to keep track nextItems on previous pages
    setNextItemStack((prev) => [...prev, lastItem]);
  };

  const onPrevPageCursorPaginate = (): void => {
    // Get the previous page's nextItem
    const secondLastItem = nextItemStack[nextItemStack.length - 2];
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      afterItem: secondLastItem,
    }));
    setNextItemStack((prev) => prev.slice(0, prev.length - 1));
  };

  const onPageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPendingInvitationsQueryParams((prev) => ({
      ...prev,
      afterItem: undefined,
      limit: newPageSize,
    }));
    resetPagination();
  };

  const pendingMemberRows = useMemo((): Row[] => {
    const ret: Row[] = [];
    for (let i = 0; i < invitedUsers.length; i++) {
      const currentInvitedUser = invitedUsers[i];
      const {
        coursesAssigned,
        email,
        fullName,
        createdOn,
        id,
        role,
      } = currentInvitedUser;
      const key = `${id}-${email}`;
      ret.push({
        key,
        cells: [
          {
            cell: (
              <NameEmailInfo>
                <img alt="user-profile" src={profileImage} />
                <div>
                  <b>{fullName}</b>
                  <UserEmail>{email}</UserEmail>
                </div>
              </NameEmailInfo>
            ),
          },
          {
            cell:
              role === 'admin'
                ? 'Institution Administrator'
                : role === 'staff'
                ? 'Course Creator'
                : 'Learner',
          },
          {
            cell: moment(createdOn).format('DD MMMM YYYY - hh:mm a Z'),
          },
          {
            cell: (
              <ConnectedCoursesAssignedPopover
                memberType="team-member"
                institutionPath={currentInstitutionPath}
                coursePaths={coursesAssigned.map((coursePath) => ({
                  coursePath,
                }))}
                currentUserId={id}
                scheduleAssignToCourse={({
                  courseRoles,
                  ids,
                  totalAssignedUsers,
                }) =>
                  scheduleAssignInvitedUsersToCourse({
                    ids,
                    courseRoles,
                    totalAssignedUsers,
                  }).then(refreshCurrentPage)
                }
              />
            ),
          },
        ],
      });
    }
    return ret;
  }, [invitedUsers, currentInstitutionPath]);

  const hideModal = useCallback((): void => {
    setShowedModalType(null);
  }, []);

  const [isBulkActionRunning, setIsBulkActionRunning] = useState(false);
  const [bulkActionError, setBulkActionError] = useState('');

  const bulkActionWrapper = useCallback(
    async <T extends { ids: string[] }>(
      bulkActionCallbackApi: (params: T) => Promise<unknown>,
      restParams: Omit<T, 'ids'>,
      pageSizeLimit: number,
      identifierKey: 'id' | 'email' = 'id'
    ) => {
      try {
        setIsBulkActionRunning(true);
        setBulkActionError('');
        if (selectAll) {
          let hasNextPageEnrolments = true;
          let afterItem: undefined | PendingInvitation;
          while (hasNextPageEnrolments) {
            // do bulk action in batch per pageSizeLimit
            // eslint-disable-next-line no-await-in-loop
            const pendingInvitations = await getPaginatedPendingInvitationsApi(
              currentInstitutionPath,
              {
                ...pendingInvitationsQueryParams,
                limit: pageSizeLimit,
                afterItem,
              }
            );
            const { hasNextPage, invitedUsers } = pendingInvitations;
            const ids = invitedUsers.map(({ [identifierKey]: id }) => id);
            // eslint-disable-next-line no-await-in-loop
            await bulkActionCallbackApi({
              ...restParams,
              ids,
            } as T);

            hasNextPageEnrolments = hasNextPage;
            afterItem = invitedUsers[invitedUsers.length - 1];
          }
        } else {
          await bulkActionCallbackApi({
            ...restParams,
            ids: selectedRows.map((key) => {
              const { id, email } = parseKeyDataPendingMember(key);
              if (identifierKey === 'email') {
                return email;
              } else {
                return id;
              }
            }),
          } as T);
        }
        setIsBulkActionRunning(false);
        hideModal();
        setSelectedRows([]);
        setPendingInvitationsQueryParams({
          ...pendingInvitationsQueryParams,
          afterItem: nextItemStack[nextItemStack.length - 1],
        });
      } catch (error) {
        setIsBulkActionRunning(false);
        setBulkActionError(error?.message);
      }
    },
    [
      selectAll,
      currentInstitutionPath,
      pendingInvitationsQueryParams,
      debouncedGetPaginatedPendingInvitations,
      hideModal,
      selectedRows,
      nextItemStack,
    ]
  );

  const onAssignToCourses = async (
    courseRoles: AssignedCourseRolesType
  ): Promise<void> => {
    try {
      setIsBulkActionRunning(true);
      setBulkActionError('');

      await scheduleAssignInvitedUsersToCourse({
        ids: selectedRows.map((key) => parseKeyDataPendingMember(key).id), // get the userId
        courseRoles,
        totalAssignedUsers: totalSelectedMembers,
        filterParams: {
          all: selectAll,
          filterByUser: pendingInvitationsQueryParams.filterByUser,
          staff: pendingInvitationsQueryParams.staff,
        },
      });
      hideModal();
    } catch (error) {
      setBulkActionError(error?.message);
    }
    setIsBulkActionRunning(false);
  };

  const ShowedModal = useMemo(() => {
    switch (showedModalType) {
      case ShowedModalType.REMOVE_FROM_INSTITUTION:
        return (
          <RemoveFromInstitutionModal
            error={bulkActionError}
            isLoading={isBulkActionRunning}
            onActionCallback={() =>
              bulkActionWrapper(uninvitePendingTeamMembers, {}, 100, 'email')
            }
            onHide={hideModal}
            totalMembersRemove={selectAll ? total : selectedRows.length}
          />
        );
      case ShowedModalType.ASSIGN_TO_COURSE:
        return (
          <AssignToCoursesModal
            errorMessage={bulkActionError}
            isAssigningFromCoursesLoading={isBulkActionRunning}
            institutionPath={currentInstitutionPath}
            onAssignToCourses={onAssignToCourses}
            onHide={hideModal}
            totalSelectedMembers={totalSelectedMembers}
            courses={allCourses}
          />
        );

      default:
        // Reset error message every modal type change
        setBulkActionError('');
        return <></>;
    }
  }, [
    showedModalType,
    hideModal,
    isBulkActionRunning,
    bulkActionWrapper,
    bulkActionError,
    selectAll,
    selectedRows,
    total,
    uninvitePendingTeamMembers,
  ]);

  const onResendEmailButtonClick = (): void => {
    bulkActionWrapper(resendInvitationEmails, {}, 100).then(() => {
      const totalSelectedUsers = selectAll ? total : selectedRows.length;
      pNotifyAlert({
        title: 'Email sent',
        text: `An invitation email has been successfully sent to ${totalSelectedUsers} pending team member${
          totalSelectedUsers > 1 ? 's' : ''
        }.`,
        type: 'success',
      });
    });
  };

  return (
    <>
      {ShowedModal}
      <FilterWrapper>
        <StyledTextField
          label="Search by name or email"
          placeholder="Start typing..."
          value={searchUserQuery}
          onValueChange={onSearchByUserValueChange}
        />
      </FilterWrapper>
      <ActionTableWrapper>
        {isLoading && (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        )}
        <ActionTable
          fixTopHeaders
          fixLeftHeaders
          itemName="member"
          headers={[
            { key: 'name_email', cell: 'Name / Email' },
            { key: 'institution_role', cell: 'Institution role' },
            {
              key: 'createdOn',
              cell: 'Date invited',
              sortKey: 'createdOn',
            },
            {
              key: 'totalCourseAssigned',
              cell: 'Courses Assigned',
              sortKey: 'totalCourseAssigned',
            },
          ]}
          rows={pendingMemberRows}
          toolbarActions={
            <Popover
              placement="bottom"
              toggle={
                <BulkActionButton>
                  Bulk actions <BulkActionSortDownIcon icon={faSortDown} />
                </BulkActionButton>
              }
              popOverInitialState={{ gutter: 0 }}
              content={
                <BulkActionContentWrapper>
                  <BulkActionContentButton
                    type="button"
                    onClick={() =>
                      setShowedModalType(ShowedModalType.ASSIGN_TO_COURSE)
                    }
                  >
                    <BulkActionIcon fixedWidth icon={faPlus} />
                    Assign to courses
                  </BulkActionContentButton>
                  <BulkActionContentButton
                    type="button"
                    onClick={onResendEmailButtonClick}
                  >
                    <BulkActionIcon fixedWidth icon={faEnvelope} />
                    Re-send email
                  </BulkActionContentButton>
                  <hr />
                  <BulkActionContentButton
                    type="button"
                    onClick={() =>
                      setShowedModalType(
                        ShowedModalType.REMOVE_FROM_INSTITUTION
                      )
                    }
                  >
                    <BulkActionIcon fixedWidth icon={faTrash} />
                    Remove from institution
                  </BulkActionContentButton>
                </BulkActionContentWrapper>
              }
              withArrow={false}
              ariaLabel="Bulk actions"
            />
          }
          selectedRows={selectedRows}
          selectAll={selectAll}
          totalItems={total}
          sortBy={sortBy}
          onSelectedRowsChanged={setSelectedRows}
          onSelectAllChanged={setSelectAll}
          onSortChanged={setSortBy}
        />
      </ActionTableWrapper>
      <PaginationContainer>
        <CursorPaginate
          pageSize={pageSize}
          totalResults={total}
          showPageSizeSelector
          onNextPage={onNextPageCursorPaginate}
          onPreviousPage={onPrevPageCursorPaginate}
          handlePageSizeChange={onPageSizeChange}
          hasNextPage={hasNextPage}
          hasPreviousPage={nextItemStack.length > 0}
        />
      </PaginationContainer>
    </>
  );
};
